import { z } from 'zod';
/**
 * Notifications (M9, ADR-012, ADR-028, `ARCHITECTURE.md` §12, `DATABASE.md` §7.9).
 *
 * Five nouns, and the separation between them is what keeps a clinic's messages
 * notify-only:
 *
 * | Shape | Authority | Table |
 * |---|---|---|
 * | `NotificationTemplate` | the clinic admin, or the platform default | `notification_templates` |
 * | `NotificationTemplateVersion` | published once, never edited | `notification_template_versions` |
 * | `Notification` | one attempt on one channel | `notifications` |
 * | `NotificationEvent` | the vendor's webhook | `notification_events` |
 * | `NotificationPreferences` | the patient | `notification_preferences` + `consents` |
 *
 * ## Nothing here is a message body a patient wrote
 *
 * A rendered body is never stored and never returned: `notifications` holds the kind,
 * the channel, the status and a hash of the recipient (`DATABASE.md` §7.9). An inbound
 * SMS is classified into a keyword and discarded (`packages/integrations/twilio`). So no
 * shape below has a field free text could be written into, in either direction.
 *
 * ## The variable allow-list is the PHI control
 *
 * `variablesAllowed` is not documentation. A template version may only reference the
 * variables its kind permits (`NOTIFICATION_VARIABLES` in
 * `apps/api/src/notifications/notification-kinds.ts`), the publish route refuses anything
 * else with `422 TEMPLATE_VARIABLE_NOT_ALLOWED`, and the renderer refuses again at send
 * time. That is how "no visit reason, no diagnosis, no member id" survives a clinic
 * admin editing their own templates (`SECURITY.md` §8, FR-NOTIF-05, FR-NOTIF-09).
 *
 * ## Addresses and numbers are hashed
 *
 * `recipientHash` is SHA-256 of the lower-cased address or the E.164 number. The address
 * itself never appears in a log, a job payload, an audit row or any shape here. The one
 * place staff see a contact point is the patient record, which is a different screen with
 * a different permission.
 */
/* ------------------------------------------------------------------- channels ---- */
export const NOTIFICATION_CHANNELS = ['email', 'sms'];
export const NotificationChannelSchema = z.enum(NOTIFICATION_CHANNELS);
/**
 * Patient-facing template keys (`DATABASE.md` §7.9, FR-NOTIF-01).
 *
 * `fee_applied` is the one addition to the sketch in `DATABASE.md`: FR-SCHED-10 raises a
 * late-cancel or no-show fee as its own event and telling the patient about it inside the
 * cancellation email would mean one template that sometimes mentions money and sometimes
 * does not - which is the kind of conditional a clinic admin edits wrongly exactly once.
 *
 * There is deliberately **no** `charge_failed` key for patients. FR-NOTIF-01 lists the
 * patient set and a declined card is not in it; FR-NOTIF-02 makes it a clinic alert. A
 * patient who is emailed "we could not take payment" cannot act on it - there is nothing
 * to click - and the clinic can.
 */
export const PATIENT_NOTIFICATION_KINDS = [
    'booking_confirmation',
    'reminder_24h',
    'reminder_30m',
    'reschedule',
    'cancellation',
    'receipt',
    'fee_applied',
    'waitlist_offer',
    /**
     * M10.3. The earlier slot went to somebody else, or the hold lapsed.
     *
     * Its own kind rather than a variant of `waitlist_offer` because FR-BOOK-16 requires
     * it ("first to accept wins; others are notified") and the two messages say opposite
     * things: one is "tap here in the next hour", the other is "nothing has changed about
     * your visit, you are still on the list". A patient who only ever receives the first
     * learns to ignore it.
     */
    'waitlist_offer_closed',
    'intake_reminder',
];
/** Staff alert template keys (FR-NOTIF-02). */
export const ADMIN_NOTIFICATION_KINDS = [
    'admin_new_booking',
    'admin_reschedule',
    'admin_cancellation',
    'admin_charge_failed',
    'admin_integration_failed',
    'admin_review_task',
    'admin_delivery_complaint',
];
/** Owned by M11.9 (user invitations), delivered by M9 (`staff_invitation` email). */
export const OTHER_NOTIFICATION_KINDS = ['staff_invitation'];
/**
 * The three messages that carry an authentication secret (M9.x, `SECURITY.md` §15).
 *
 * They are notification kinds so that a magic link leaves the platform through the same
 * door, the same PHI guard and the same `notifications` row as every other message - a
 * clinic that cannot see whether a sign-in email was accepted has no way to answer "I
 * never got it".
 *
 * They are **platform-owned**: `templatePairs()` (`notification-kinds.ts`) excludes
 * them, so they never appear in a clinic's template editor and no tenant version can
 * exist for them. A clinic admin who removed `{{signInUrl}}` from the magic-link body
 * would be publishing a sign-in email nobody can sign in from, and the first person to
 * find out would be a locked-out colleague.
 *
 * Nothing about them is ever queued or stored: the secret lives in memory for the
 * duration of one synchronous send (`NotificationsService.sendImmediate`).
 */
export const AUTH_NOTIFICATION_KINDS = [
    'auth_magic_link',
    'auth_otp',
    'auth_password_reset',
];
export const NOTIFICATION_KINDS = [
    ...PATIENT_NOTIFICATION_KINDS,
    ...ADMIN_NOTIFICATION_KINDS,
    ...OTHER_NOTIFICATION_KINDS,
    ...AUTH_NOTIFICATION_KINDS,
];
export const NotificationKindSchema = z.enum(NOTIFICATION_KINDS);
/**
 * `notifications.status` (`DATABASE.md` §7.9, `ck_notifications_status`).
 *
 * `suppressed` and `suppressed_ehr` are different facts and must not be merged. The
 * first is the **provider** refusing a message - a bounced address, an opted-out number
 * - and it is a contact a staff member can correct. The second is this platform
 * *choosing* not to send, because the clinic told us their EHR owns that message
 * (FR-NOTIF-10, M9.13). Nobody can act on the second, and the honest reading of it is
 * "your EHR sent this; we cannot see whether it arrived" - which is the sentence the
 * delivery log shows, and which would be a lie on a `suppressed` row.
 */
export const NOTIFICATION_STATUSES = [
    'scheduled',
    'queued',
    'sent',
    'delivered',
    'failed',
    'suppressed',
    'suppressed_ehr',
    'cancelled',
];
export const NotificationStatusSchema = z.enum(NOTIFICATION_STATUSES);
/**
 * `notification_events.event`, both channels in one list (FR-NOTIF-06).
 *
 * The first six are Mailgun's normalised set; the rest are the SMS half ADR-028 added.
 * `undelivered` and `failed` stay apart because a clinic does different things about
 * them - a carrier refusing a message is a contact a staff member can correct, a Twilio
 * failure is ours (`packages/integrations/twilio/README.md`).
 */
export const NOTIFICATION_EVENT_TYPES = [
    'accepted',
    'delivered',
    'temporary_fail',
    'permanent_fail',
    'complained',
    'unsubscribed',
    'queued',
    'sent',
    'undelivered',
    'failed',
    'opted_out',
    'opted_in',
    'help_requested',
    'inbound',
];
export const NotificationEventTypeSchema = z.enum(NOTIFICATION_EVENT_TYPES);
/* ------------------------------------------------------------------ templates ---- */
/**
 * Where the body a send will render comes from.
 *
 * `platform_default` is a complete answer, not a missing row: a clinic that has never
 * opened the template editor still sends correct confirmations, because the built-in
 * bodies ship in code. A tenant version overrides it the moment one is published.
 */
export const TEMPLATE_SOURCES = ['tenant', 'platform_default'];
export const TemplateSourceSchema = z.enum(TEMPLATE_SOURCES);
export const NotificationTemplateSchema = z
    .object({
    id: z.uuid(),
    key: NotificationKindSchema,
    channel: NotificationChannelSchema,
    name: z.string().max(120),
    isActive: z.boolean().default(true),
    /** The published version a send uses today; `null` while the default is in force. */
    currentVersion: z.int().min(1).nullable().default(null),
    source: TemplateSourceSchema,
    /** Every variable this kind and channel may reference. The editor's palette. */
    variablesAllowed: z.array(z.string().max(60)).max(40),
    updatedAt: z.iso.datetime(),
})
    .strict();
export const NotificationTemplateVersionSchema = z
    .object({
    id: z.uuid(),
    version: z.int().min(1),
    subject: z.string().max(300).nullable().default(null),
    bodyHtml: z.string().nullable().default(null),
    bodyText: z.string().nullable().default(null),
    variablesAllowed: z.array(z.string().max(60)).max(40),
    changelog: z.string().max(500).nullable().default(null),
    publishedBy: z.string().max(128).nullable().default(null),
    isCurrent: z.boolean().default(false),
    createdAt: z.iso.datetime(),
})
    .strict();
export const NotificationTemplateListResponseSchema = z
    .object({ data: z.object({ templates: z.array(NotificationTemplateSchema) }).strict() })
    .strict();
export const NotificationTemplateDetailResponseSchema = z
    .object({
    data: z
        .object({
        template: NotificationTemplateSchema,
        versions: z.array(NotificationTemplateVersionSchema),
        /** The built-in body, so the editor can offer "reset to the platform default". */
        platformDefault: z
            .object({
            subject: z.string().max(300).nullable().default(null),
            bodyHtml: z.string().nullable().default(null),
            bodyText: z.string().nullable().default(null),
        })
            .strict(),
    })
        .strict(),
})
    .strict();
/**
 * `POST /admin/notification-templates/:id/versions` (API.md §2.9).
 *
 * A version is created, never edited: `notification_template_versions` carries an
 * immutability trigger (`DATABASE.md` §4). Creating one does **not** activate it - that
 * is a second, deliberate call, so a half-written body cannot become the live one by
 * pressing save.
 */
export const CreateTemplateVersionRequestSchema = z
    .object({
    subject: z.string().min(1).max(300).optional(),
    bodyHtml: z.string().min(1).max(60_000).optional(),
    bodyText: z.string().min(1).max(20_000),
    changelog: z.string().max(500).optional(),
    /** Activate it in the same call. Defaults to false: publishing is its own decision. */
    activate: z.boolean().default(false),
})
    .strict();
export const NotificationTemplateVersionResponseSchema = z
    .object({
    data: z
        .object({
        template: NotificationTemplateSchema,
        version: NotificationTemplateVersionSchema,
    })
        .strict(),
})
    .strict();
/**
 * `POST /admin/notification-templates/:id/send-test` (API.md §2.9).
 *
 * Synthetic sample data only, and the recipient defaults to the caller. `toUserEmail`
 * must belong to a user of this tenant: a test send is a send, and a route that mailed
 * an arbitrary address would be an open relay with a clinic's reputation behind it.
 */
export const SendTestNotificationRequestSchema = z
    .object({
    version: z.int().min(1).optional(),
    toUserEmail: z.email().max(320).optional(),
})
    .strict();
/* -------------------------------------------------------------- delivery log ---- */
export const NotificationSchema = z
    .object({
    id: z.uuid(),
    kind: z.string().max(60),
    channel: NotificationChannelSchema,
    recipientKind: z.enum(['patient', 'staff']),
    status: NotificationStatusSchema,
    patientId: z.uuid().nullable().default(null),
    userId: z.string().max(128).nullable().default(null),
    appointmentId: z.uuid().nullable().default(null),
    /** SHA-256 of the address or number. Never the address (`SECURITY.md` §8). */
    recipientHash: z.string().max(64),
    providerMessageId: z.string().max(200).nullable().default(null),
    templateVersion: z.int().min(1).nullable().default(null),
    /**
     * When the message was meant to land: the reminder's target, or - for an immediate
     * send - the moment it was queued. **Never null for a message this platform
     * created**, so an admin table can render it directly rather than falling back
     * through `sentAt ?? scheduledFor ?? createdAt`. Nullable only because rows written
     * before M9's follow-up have no value (`ADMIN_OPERATIONS_UI.md` §6 item 20).
     */
    scheduledFor: z.iso.datetime().nullable().default(null),
    sentAt: z.iso.datetime().nullable().default(null),
    /** A class, never a vendor sentence: `card_declined`, `suppressed`, `opted_out`. */
    errorClass: z.string().max(80).nullable().default(null),
    createdAt: z.iso.datetime(),
})
    .strict();
export const NotificationEventSchema = z
    .object({
    id: z.uuid(),
    notificationId: z.uuid().nullable().default(null),
    event: NotificationEventTypeSchema,
    reason: z.string().max(200).nullable().default(null),
    occurredAt: z.iso.datetime(),
})
    .strict();
/** `GET /admin/notifications` (API.md §2.9). Every filter is optional and ANDed. */
export const NotificationLogQuerySchema = z
    .object({
    kind: z.string().max(60).optional(),
    channel: NotificationChannelSchema.optional(),
    status: NotificationStatusSchema.optional(),
    patientId: z.uuid().optional(),
    appointmentId: z.uuid().optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    cursor: z.string().max(200).optional(),
})
    .strict();
export const NotificationListResponseSchema = z
    .object({
    data: z
        .object({
        notifications: z.array(NotificationSchema),
        nextCursor: z.string().max(200).nullable().default(null),
    })
        .strict(),
})
    .strict();
export const NotificationDetailResponseSchema = z
    .object({
    data: z
        .object({
        notification: NotificationSchema,
        events: z.array(NotificationEventSchema),
    })
        .strict(),
})
    .strict();
export const NotificationResponseSchema = z
    .object({ data: z.object({ notification: NotificationSchema }).strict() })
    .strict();
/* ---------------------------------------------------------------- reminders ---- */
/**
 * One row of the `reminder_jobs` view (`DATABASE.md` §7.9).
 *
 * Read straight from pg-boss rather than from a table of our own, because a copy would
 * have to be kept in step with the queue and the copy is always the one that is wrong.
 */
export const ReminderJobSchema = z
    .object({
    jobId: z.string().max(64),
    kind: z.enum(['t24h', 't30m', 'waitlist_offer']),
    appointmentId: z.uuid().nullable().default(null),
    state: z.string().max(20),
    startAfter: z.iso.datetime(),
    retryCount: z.int().min(0).default(0),
})
    .strict();
export const ReminderListQuerySchema = z
    .object({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
})
    .strict();
export const ReminderListResponseSchema = z
    .object({ data: z.object({ reminders: z.array(ReminderJobSchema) }).strict() })
    .strict();
/* --------------------------------------------------------- staff alert routing ---- */
/**
 * The alerts a clinic can route (FR-NOTIF-02, Q-P07).
 *
 * The keys are the `admin_*` template kinds without the prefix, because the settings
 * screen talks about events and the template editor talks about templates, and one word
 * doing both jobs is how they drift.
 */
export const NOTIFICATION_ALERT_KINDS = [
    'new_booking',
    'reschedule',
    'cancellation',
    'charge_failed',
    'integration_failed',
    'review_task',
    'delivery_complaint',
];
export const NotificationAlertKindSchema = z.enum(NOTIFICATION_ALERT_KINDS);
/**
 * Who hears about one alert.
 *
 * Two lists rather than one, because Q-P07's answer is "a shared inbox" and a shared
 * inbox is not a user account. `userIds` are staff of this tenant and become
 * `notification_preferences` rows (API.md §2.9); `emails` are addresses the clinic named
 * and live in `tenants.settings.notifications.alertEmails`. A user who is deactivated
 * stops being alerted without anybody editing a list; an address does not, which is
 * exactly the difference between the two and the reason both exist.
 */
export const AlertRecipientsSchema = z
    .object({
    userIds: z.array(z.string().min(1).max(128)).max(50).default([]),
    emails: z.array(z.email().max(320)).max(20).default([]),
})
    .strict();
/* --------------------------------------------------- reminder ownership (M9.13) ---- */
/**
 * Who sends the appointment timeline (FR-NOTIF-10, `_research/tebra-communications.md` §4).
 *
 * `platform` is the default and the recommendation. The alternative exists because a
 * clinic whose EHR already messages its patients would otherwise get two of everything
 * - and because that clinic, not us, owns the decision. It is not a feature we expect
 * anybody to turn on: the research brief's §3 lists what is lost with it, and the admin
 * screen prints that list before the switch is saved.
 */
export const REMINDER_OWNERS = ['platform', 'ehr'];
export const ReminderOwnerSchema = z.enum(REMINDER_OWNERS);
/**
 * The only kinds an EHR may be given, and the list is closed on purpose.
 *
 * Every other kind carries a per-appointment token or has no EHR equivalent at all:
 * `intake_reminder`, `waitlist_offer`, `waitlist_offer_closed`, `receipt`,
 * `fee_applied`, every `admin_*` alert, `staff_invitation` and the three `auth_*` mails.
 * Tebra's templates have no macro for an arbitrary URL (§1.2), so handing one of those
 * over would not delegate the message - it would delete it.
 *
 * This list is the schema's half of the guard; `notification-kinds.ts` enforces the same
 * set in code, so a settings block edited straight into `tenants.settings` by hand still
 * cannot suppress a receipt.
 */
export const EHR_SUPPRESSIBLE_KINDS = [
    'booking_confirmation',
    'reminder_24h',
    'reminder_30m',
    'reschedule',
    'cancellation',
];
export const EhrSuppressibleKindSchema = z.enum(EHR_SUPPRESSIBLE_KINDS);
export const ReminderOwnershipSchema = z
    .object({
    reminderOwner: ReminderOwnerSchema.default('platform'),
    /** Honoured only when `reminderOwner` is `ehr`. Empty means "still send everything". */
    ehrOwnedKinds: z
        .array(EhrSuppressibleKindSchema)
        .max(EHR_SUPPRESSIBLE_KINDS.length)
        .default([]),
    /** The label the delivery log prints: "Sent by your EHR (Tebra)". */
    ehrName: z.string().max(60).nullable().default(null),
})
    .strict();
export const NotificationSettingsSchema = z
    .object({
    alerts: z
        .object({
        new_booking: AlertRecipientsSchema,
        reschedule: AlertRecipientsSchema,
        cancellation: AlertRecipientsSchema,
        charge_failed: AlertRecipientsSchema,
        integration_failed: AlertRecipientsSchema,
        review_task: AlertRecipientsSchema,
        delivery_complaint: AlertRecipientsSchema,
    })
        .strict(),
    /** Per-tenant SMS switch; the `sms` plan feature gates it above this (§17). */
    smsEnabled: z.boolean().default(false),
    /**
     * The window an SMS reminder may land in, in the **patient's** zone. Absent means
     * no restriction. A reminder outside it is moved to the next open minute, never
     * dropped: an unsent reminder is worse than a late one for T-24 h, and T-30 min is
     * never quiet-hours-suppressed because a visit at 07:00 is a visit at 07:00.
     */
    smsQuietHours: z
        .object({
        start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
        end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    })
        .strict()
        .nullable()
        .default(null),
    /**
     * Who owns the appointment timeline (M9.13, FR-NOTIF-10).
     *
     * Additive: a clinic that has never opened the screen reads back
     * `{ reminderOwner: 'platform', ehrOwnedKinds: [], ehrName: null }`, which is what
     * every tenant did before this block existed.
     */
    reminderOwner: ReminderOwnerSchema.default('platform'),
    ehrOwnedKinds: ReminderOwnershipSchema.shape.ehrOwnedKinds,
    ehrName: ReminderOwnershipSchema.shape.ehrName,
})
    .strict();
const EMPTY_RECIPIENTS = Object.freeze({ userIds: [], emails: [] });
export const NOTIFICATION_SETTINGS_DEFAULTS = Object.freeze({
    alerts: Object.freeze({
        new_booking: EMPTY_RECIPIENTS,
        reschedule: EMPTY_RECIPIENTS,
        cancellation: EMPTY_RECIPIENTS,
        charge_failed: EMPTY_RECIPIENTS,
        integration_failed: EMPTY_RECIPIENTS,
        review_task: EMPTY_RECIPIENTS,
        delivery_complaint: EMPTY_RECIPIENTS,
    }),
    smsEnabled: false,
    smsQuietHours: null,
    reminderOwner: 'platform',
    ehrOwnedKinds: Object.freeze([]),
    ehrName: null,
});
export const UpdateNotificationSettingsRequestSchema = z
    .object({
    alerts: z
        .object({
        new_booking: AlertRecipientsSchema.optional(),
        reschedule: AlertRecipientsSchema.optional(),
        cancellation: AlertRecipientsSchema.optional(),
        charge_failed: AlertRecipientsSchema.optional(),
        integration_failed: AlertRecipientsSchema.optional(),
        review_task: AlertRecipientsSchema.optional(),
        delivery_complaint: AlertRecipientsSchema.optional(),
    })
        .strict()
        .optional(),
    smsEnabled: z.boolean().optional(),
    smsQuietHours: NotificationSettingsSchema.shape.smsQuietHours.optional(),
    /**
     * M9.13. Each field is independently optional, like every other field on this
     * patch: the ownership screen and the alert-routing screen are two sections of one
     * settings page, and a PUT that rendered only one of them must not clear the other.
     */
    reminderOwner: ReminderOwnerSchema.optional(),
    ehrOwnedKinds: z.array(EhrSuppressibleKindSchema).max(EHR_SUPPRESSIBLE_KINDS.length).optional(),
    ehrName: z.string().max(60).nullable().optional(),
})
    .strict();
export const NotificationSettingsResponseSchema = z
    .object({ data: z.object({ settings: NotificationSettingsSchema }).strict() })
    .strict();
/* ------------------------------------------------------ patient preferences ---- */
/**
 * `GET`/`PUT /patient/notification-preferences` (API.md §2.4, FR-NOTIF-08).
 *
 * ## What cannot be switched off
 *
 * Confirmations, reschedules and cancellations by **email** are transactional: they are
 * how a patient learns that the thing they asked for happened, and the shape has no
 * field for turning them off. Reminders can be. SMS as a whole can be, and is off until
 * an opt-in exists.
 *
 * ## `smsConsent` is a record, not a toggle
 *
 * The toggle is `sms.enabled`. The consent is the immutable `consents` row behind it,
 * with the wording version the patient actually saw. Turning the toggle on without a
 * consent record is refused - an opt-in the clinic cannot show the text of is not an
 * opt-in (45 CFR 164.508 for marketing, TCPA for the rest).
 */
export const NotificationPreferencesSchema = z
    .object({
    email: z
        .object({
        reminders: z.boolean().default(true),
    })
        .strict(),
    sms: z
        .object({
        enabled: z.boolean().default(false),
        reminders: z.boolean().default(true),
    })
        .strict(),
    /** False when the clinic has no SMS install, no plan feature, or has switched it off. */
    smsAvailable: z.boolean().default(false),
    /** False when the patient record carries no mobile number to send to. */
    smsNumberOnFile: z.boolean().default(false),
    smsConsent: z
        .object({
        granted: z.boolean().default(false),
        documentVersion: z.string().max(40).nullable().default(null),
        acceptedAt: z.iso.datetime().nullable().default(null),
    })
        .strict(),
    /** Set by a STOP reply. SMS stays off until the patient opts in again. */
    smsOptedOutAt: z.iso.datetime().nullable().default(null),
    marketingConsent: z
        .object({
        granted: z.boolean().default(false),
        documentVersion: z.string().max(40).nullable().default(null),
        acceptedAt: z.iso.datetime().nullable().default(null),
    })
        .strict(),
})
    .strict();
export const UpdateNotificationPreferencesRequestSchema = z
    .object({
    email: z.object({ reminders: z.boolean() }).strict().optional(),
    sms: z
        .object({
        enabled: z.boolean().optional(),
        reminders: z.boolean().optional(),
    })
        .strict()
        .optional(),
    /**
     * Withdrawing marketing authorization from the portal (FR-CRM-04). It cannot be
     * *granted* here: a grant needs the wording, and the wording lives in the funnel and
     * in the clinic's settings, not in a boolean.
     */
    marketingConsentGranted: z.literal(false).optional(),
})
    .strict();
export const NotificationPreferencesResponseSchema = z
    .object({ data: z.object({ preferences: NotificationPreferencesSchema }).strict() })
    .strict();
/** `GET /patient/notifications` (API.md §2.4): status only, never a body. */
export const PatientNotificationSchema = z
    .object({
    id: z.uuid(),
    kind: z.string().max(60),
    channel: NotificationChannelSchema,
    status: NotificationStatusSchema,
    appointmentId: z.uuid().nullable().default(null),
    sentAt: z.iso.datetime().nullable().default(null),
    createdAt: z.iso.datetime(),
})
    .strict();
export const PatientNotificationListResponseSchema = z
    .object({
    data: z
        .object({
        notifications: z.array(PatientNotificationSchema),
        nextCursor: z.string().max(200).nullable().default(null),
    })
        .strict(),
})
    .strict();
/* ------------------------------------------------------------------ consents ---- */
/**
 * `consents.kind` (`DATABASE.md` §7.4, `ck_consents_kind`).
 *
 * `marketing_authorization` is the M9 addition: a HIPAA 45 CFR 164.508 authorization is
 * a different legal instrument from a treatment communication opt-in, and filing both
 * under one word would make the CRM gate unprovable (ADR-028, FR-CRM-03). The funnel
 * spells it `marketing` on the draft (`DraftConsentKind`); it becomes this at confirm.
 */
export const CONSENT_KINDS = [
    'telehealth',
    'hipaa_notice',
    'financial',
    'unsecured_email',
    'sms',
    'marketing_authorization',
];
export const ConsentKindSchema = z.enum(CONSENT_KINDS);
/**
 * One acceptance, as any surface may see it.
 *
 * No ip hash and no user agent: both are stored on the row (`DATABASE.md` §7.4) and
 * neither is anybody's business on a read. An IP address is also a location and a
 * subscriber (`SECURITY.md` §15).
 */
export const ConsentRecordSchema = z
    .object({
    kind: ConsentKindSchema,
    documentVersion: z.string().max(40),
    acceptedAt: z.iso.datetime(),
    /** False once a preference or a STOP withdrew it. The row itself never changes. */
    active: z.boolean().default(true),
})
    .strict();
export const ConsentListResponseSchema = z
    .object({ data: z.object({ consents: z.array(ConsentRecordSchema) }).strict() })
    .strict();
/**
 * The consent wording behind a version, as any surface may read it (M9.7 follow-up).
 *
 * `ConsentRecord` says *that* somebody agreed and to which version. This says what that
 * version **is**, so a portal can print the sentence rather than the string `sms-v1.0`,
 * and so a client can show the wording *before* anybody agrees to it.
 *
 * `published` is the field that decides whether the document may be accepted. A clinic
 * that has not written its own wording gets an entry with `published: false`, a `null`
 * `version` and a platform *placeholder* `text` - a plain description of what the opt-in
 * would do, never legal copy written on a clinic's behalf (`DATABASE.md` §7.4). There is
 * nothing to post: `POST /patient/consents` refuses any version the tenant has not
 * published, and an unpublished document has no version to send.
 */
export const ConsentDocumentSchema = z
    .object({
    kind: ConsentKindSchema,
    /** The version a `consents` row would record. `null` when nothing is published. */
    version: z.string().max(40).nullable(),
    /** The clinic's wording, or the platform placeholder when `published` is false. */
    text: z.string().nullable(),
    /** Whether the funnel may proceed without it ticked. */
    required: z.boolean(),
    /** True only when the clinic itself published this wording. */
    published: z.boolean(),
})
    .strict();
export const ConsentDocumentListResponseSchema = z
    .object({ data: z.object({ documents: z.array(ConsentDocumentSchema) }).strict() })
    .strict();
/**
 * The consents a signed-in patient may grant themselves (`POST /patient/consents`).
 *
 * Two of the six, and the other four are absent on purpose. `telehealth`, `hipaa_notice`,
 * `financial` and `unsecured_email` are shown at the point they apply - the funnel, the
 * card step - and a portal that could grant them out of that context would be recording
 * an agreement to something the patient was not doing at the time.
 */
export const PORTAL_CONSENT_KINDS = ['sms', 'marketing_authorization'];
export const PortalConsentKindSchema = z.enum(PORTAL_CONSENT_KINDS);
/**
 * `POST /patient/consents`.
 *
 * `accepted` can only be `true`, and that is the contract rather than a redundant field:
 * `consents` is append-only evidence, so there is no such thing as posting a withdrawal
 * here. Turning something off is `PUT /patient/notification-preferences`, which is where
 * current state lives (`consents.service.ts`). A client sending `false` gets a `400`
 * naming the field rather than a silent no-op it would report as a bug.
 *
 * `documentVersion` has no default and is never invented: a consent whose wording nobody
 * can reproduce is not evidence, so the version must be one the tenant has published
 * (`GET /patient/consents/documents` is the list).
 */
export const RecordConsentRequestSchema = z
    .object({
    kind: PortalConsentKindSchema,
    documentVersion: z.string().min(1).max(40),
    accepted: z.literal(true),
})
    .strict();
//# sourceMappingURL=notifications.js.map